import { JustRightHeightModal } from 'components/styled/scoped';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import { css } from '@emotion/css';
import { Alert, Button, Checkbox, Input, Select, Space, Switch, Typography, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import type {
  ProductKnowledgeScopeProduct,
  ProductKnowledgeScopeSearchResult,
  ProductStoreScope,
} from '../../model/types';
import {
  productSelectionKey,
  mergeSelectionProducts,
  selectableKeys,
  skuSelectionKey,
} from '../../model/selection';
import { ProductSkuCard } from '../ScopeProductCard';
import { StoreScopePickerSelect } from '../StoreScopePickerSelect';
import { ScopePagination } from '../ScopePagination';

const PAGE_SIZE = 20;
const BULK_PAGE_SIZE = 200;

/** 对齐 perseus PRODUCT_SEARCH_TYPES 顺序；批量匹配入口刻意不含 TITLE */
type SearchType = 'TITLE' | 'SPU_ID' | 'SELLER_SPU' | 'SELLER_SKU' | 'SKU_ID';

export interface ProductPickerSearchInput {
  pageNo: number;
  pageSize: number;
  itemSearch?: {
    storeScopes?: ProductStoreScope[];
    keywordType?: SearchType;
    keyword?: string;
  };
}

/** onConfirm 收到的选择：known 里的商品 + 全部选中 key（含义由 keyMode 决定） */
export interface ProductPickerSelection {
  products: ProductKnowledgeScopeProduct[];
  selectedKeys: Set<string>;
}

const createSearchTypeOptions = (t: (key: string) => string) =>
  [
    { label: t('productKnowledge_filter_product_name'), value: 'TITLE' },
    { label: t('productKnowledge_filter_product_id'), value: 'SPU_ID' },
    { label: t('productKnowledge_filter_seller_spu'), value: 'SELLER_SPU' },
    { label: t('productKnowledge_filter_sku_code'), value: 'SELLER_SKU' },
    { label: t('productKnowledge_scope_editor_search_type_sku_id'), value: 'SKU_ID' },
  ] as const;

const optionAlignDropdownClassName = css`
  .ant-select-item-option-content {
    padding-left: 11px;
  }
`;

const bodyClassName = css`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
`;

/**
 * 弹窗 body 固定为视口内高度并关闭整体滚动：
 * 筛选区/分页区固定，仅中间商品网格滚动（bodyClassName 内 .document-scope-scroll）；
 * header/body/footer 统一 16px 水平节奏，清掉默认 24px 与 styled margin 的叠留白
 */
const pickerModalClassName = css`
  && .ant-modal-body {
    display: flex;
    flex-direction: column;
    height: calc(94vh - 108px);
    overflow: hidden;
    margin: 0;
    padding: 12px 16px 8px;
  }

  && .ant-modal-header {
    padding: 16px 16px;
  }

  && .ant-modal-footer {
    padding: 8px 16px;
  }

  && .ant-modal-close {
    right: 16px;
  }
`;

const listClassName = css`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;

  .document-scope-selected-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
  }

  .document-scope-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    padding: 4px 0 12px;
    border-bottom: 1px solid var(--fett-border-default, #f0f0f0);
  }

  .document-scope-toolbar-right {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }

  .document-scope-only-selected {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .document-scope-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .document-scope-products {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    align-content: start;
  }

  .document-scope-empty {
    padding: 32px 0;
    text-align: center;
  }
`;

interface LoadPageOverrides {
  size?: number;
  storeScopes?: ProductStoreScope[];
  keyword?: string;
  type?: SearchType;
}

const splitKeywordQueries = (bulkKeywords: string) =>
  bulkKeywords
    .split(/[,\n]/)
    .map((value) => value.trim())
    .filter(Boolean);

export interface ProductPickerDrawerProps {
  visible: boolean;
  title: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (selection: ProductPickerSelection) => Promise<void> | void;
  search: (input: ProductPickerSearchInput) => Promise<ProductKnowledgeScopeSearchResult>;
  /** sku=按 SKU 勾选（关联维护/文档任务）；product=整商品勾选（商详任务） */
  keyMode: 'sku' | 'product';
  /** 确认前的提示（并发冲突/已保存刷新失败等） */
  notice?: ReactNode;
  confirmLoading?: boolean;
  /** 提供时取代内部「无选择/查询失败禁用」逻辑（如「已保存待刷新」的重试） */
  confirmDisabled?: boolean;
  /** 父级忙（保存中/待刷新）时冻结工具栏与卡片 */
  frozen?: boolean;
  /** 自定义宽度（嵌套子抽屉比父层窄一档，对齐 perseus 72px 偏移） */
  width?: number;
  /** 变化时重新加载首页（嵌套添加成功后刷新父层列表） */
  reloadKey?: number;
  /** 为 true 时禁止关闭（子层打开期间屏蔽父层关闭/遮罩点击） */
  closeDisabled?: boolean;
  /** 头部附加操作（ASSOCIATED 层的「添加关联商品」入口） */
  headerExtra?: ReactNode;
}

/**
 * 商品选品抽屉（对齐 perseus ProductSelectionDialog 的共用形态）：
 * 编辑知识的关联维护与任务创建选品共用同一个抽屉，仅注入搜索接口、勾选粒度与确认文案
 */
export function ProductPickerDrawer({
  visible,
  title,
  confirmLabel,
  onClose,
  onConfirm,
  search,
  keyMode,
  notice,
  confirmLoading = false,
  confirmDisabled,
  frozen = false,
  width = 960,
  reloadKey,
  closeDisabled = false,
  headerExtra,
}: ProductPickerDrawerProps) {
  const { t } = useTranslate();
  const searchTypeOptions = createSearchTypeOptions(t);
  const [searchType, setSearchType] = useState<SearchType>('SPU_ID');
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedStoreScopes, setAppliedStoreScopes] = useState<ProductStoreScope[]>([]);
  const [items, setItems] = useState<ProductKnowledgeScopeProduct[]>([]);
  const [known, setKnown] = useState<ProductKnowledgeScopeProduct[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const [onlySelected, setOnlySelected] = useState(false);
  const [expandedSpus, setExpandedSpus] = useState<Set<string>>(new Set());
  const [bulkVisible, setBulkVisible] = useState(false);
  const [bulkMatchType, setBulkMatchType] = useState<SearchType>('SPU_ID');
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [bulkMatching, setBulkMatching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const sequence = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;
  const loadPageRef = useRef<(page: number, overrides?: LoadPageOverrides) => Promise<void>>();

  const keysOf = (product: ProductKnowledgeScopeProduct) =>
    keyMode === 'sku' ? selectableKeys(product) : [productSelectionKey(product)];

  const loadPage = useCallback(
    async (page: number, overrides?: LoadPageOverrides) => {
      const id = ++sequence.current;
      setLoading(true);
      setPageError(false);
      // 刚变更的条件尚未进 state，由调用方显式覆盖，避免闭包读到旧值
      const effectiveStoreScopes = overrides?.storeScopes ?? appliedStoreScopes;
      const effectiveKeyword = overrides?.keyword ?? appliedKeyword;
      const effectiveType = overrides?.type ?? searchType;
      const itemSearch = {
        ...(effectiveStoreScopes.length ? { storeScopes: effectiveStoreScopes } : {}),
        ...(effectiveKeyword ? { keywordType: effectiveType, keyword: effectiveKeyword } : {}),
      };
      try {
        const result = await searchRef.current({
          pageNo: page,
          pageSize: overrides?.size ?? pageSize,
          itemSearch: Object.keys(itemSearch).length ? itemSearch : undefined,
        });
        if (id !== sequence.current) return;
        // 对齐 perseus：页码超出末页（末页记录被删）时自动回退末页重查
        const size = overrides?.size ?? pageSize;
        const lastPage = Math.max(1, Math.ceil(result.total / size));
        if (!result.items.length && result.total > 0 && page > lastPage) {
          setLoading(false);
          return loadPageRef.current?.(lastPage, overrides);
        }
        setItems(result.items);
        // known 合并保留旧 SKU 列表（对齐 perseus mergeProducts：skus 按 skuId 做 union，
        // 避免后续搜索返回较少 SKU 时丢失已勾选 SKU 的归属商品）
        setKnown((current) => mergeSelectionProducts(current, result.items));
        setPageNo(result.pageNo);
        setTotal(result.total);
        setHasNext(result.hasNext);
      } catch (error) {
        if (id === sequence.current) setPageError(true);
        throw error;
      } finally {
        if (id === sequence.current) setLoading(false);
      }
    },
    [pageSize, appliedStoreScopes, appliedKeyword, searchType],
  );
  loadPageRef.current = loadPage;

  const applySearch = () => {
    const kw = keyword.trim();
    setAppliedKeyword(kw);
    void loadPage(1, { keyword: kw }).catch(() => undefined);
  };

  useEffect(() => {
    if (!visible) return;
    void loadPage(1).catch(() => undefined);
    return () => {
      sequence.current += 1;
    };
    // 仅在抽屉打开时首查；条件变化由 applySearch / 店铺 / pageSize 回调显式触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // 嵌套添加成功后父层刷新首页（对齐 perseus reloadKey）
  const firstReloadRef = useRef(true);
  useEffect(() => {
    if (firstReloadRef.current) {
      firstReloadRef.current = false;
      return;
    }
    if (!visible || reloadKey === undefined) return;
    void loadPage(1).catch(() => undefined);
    if (onlySelected) setOnlySelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleSpu = (product: ProductKnowledgeScopeProduct) => {
    const keys = keysOf(product);
    setSelected((current) => {
      const next = new Set(current);
      const remove = keys.every((key) => current.has(key));
      keys.forEach((key) => {
        if (remove) next.delete(key);
        else next.add(key);
      });
      return next;
    });
  };

  const picked = known.filter((product) => keysOf(product).some((key) => selected.has(key)));
  const pageKeys = items.flatMap(keysOf);
  const pageSelected = pageKeys.length > 0 && pageKeys.every((key) => selected.has(key));

  const selectPage = () =>
    setSelected((current) => {
      const next = new Set(current);
      pageKeys.forEach((key) => next.add(key));
      return next;
    });

  const clearPage = () =>
    setSelected((current) => {
      const next = new Set(current);
      pageKeys.forEach((key) => next.delete(key));
      return next;
    });

  const allExpanded =
    items.length > 0 && items.every((product) => expandedSpus.has(productSelectionKey(product)));

  const toggleAllExpanded = () =>
    setExpandedSpus((current) => {
      const next = new Set(current);
      if (allExpanded) items.forEach((product) => next.delete(productSelectionKey(product)));
      else items.forEach((product) => next.add(productSelectionKey(product)));
      return next;
    });

  // 对齐 perseus selectedCounts：已选商品数 / 已选 SKU 数两个维度
  const selectedSpuCount = known.filter((product) =>
    keysOf(product).some((key) => selected.has(key)),
  ).length;
  const selectedSkuCount = known.reduce(
    (total_, product) =>
      keyMode === 'sku' && product.skus.length
        ? total_ +
          product.skus.filter((sku) => selected.has(skuSelectionKey(product, sku.skuId))).length
        : total_,
    0,
  );

  const submitBulk = async () => {
    const queries = splitKeywordQueries(bulkKeywords);
    if (!queries.length) return;
    setBulkMatching(true);
    try {
      const additions: ProductKnowledgeScopeProduct[] = [];
      for (const kw of queries) {
        let page = 1;
        let more = true;
        while (more) {
          const result = await searchRef.current({
            pageNo: page,
            pageSize: BULK_PAGE_SIZE,
            itemSearch: {
              ...(appliedStoreScopes.length ? { storeScopes: appliedStoreScopes } : {}),
              keywordType: bulkMatchType,
              keyword: kw,
            },
          });
          additions.push(...result.items);
          more = result.hasNext && result.items.length > 0;
          page += 1;
        }
      }
      setKnown((current) => mergeSelectionProducts(current, additions));
      setSelected((current) => {
        const next = new Set(current);
        additions.flatMap((product) => keysOf(product)).forEach((key) => next.add(key));
        return next;
      });
      if (!additions.length) message.warning(t('productKnowledge_scope_editor_no_match'));
      setBulkVisible(false);
      setBulkKeywords('');
    } catch {
      message.error(t('productKnowledge_scope_editor_search_failed'));
    } finally {
      setBulkMatching(false);
    }
  };

  const handleConfirm = async () => {
    if (confirming || confirmLoading) return;
    const payload: ProductPickerSelection = {
      products: picked,
      selectedKeys: new Set(selected),
    };
    // 捕获载荷后立即清空选择：写入成功不重放；失败/冲突由父级刷新后让用户重新勾选
    setSelected(new Set());
    setConfirming(true);
    try {
      await onConfirm(payload);
    } catch {
      // 父级负责错误提示，抽屉保持打开
    } finally {
      setConfirming(false);
    }
  };

  const busy = loading || bulkMatching || frozen;
  const internalConfirmDisabled = loading || bulkMatching || pageError || !selected.size;
  const cardsDisabled = busy || confirming;

  const visibleProducts = onlySelected ? picked : items;

  const requestClose = () => {
    if (closeDisabled) return;
    onClose();
  };

  return (
    <JustRightHeightModal
      centered
      className={pickerModalClassName}
      visible={visible}
      title={title}
      width={width}
      destroyOnClose
      maskClosable={!closeDisabled}
      closable={!closeDisabled}
      keyboard={!closeDisabled}
      onCancel={requestClose}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            disabled={closeDisabled || confirming || bulkMatching || confirmLoading}
            onClick={requestClose}
          >
            {t('productKnowledge_common_cancel')}
          </Button>
          <Button
            type="primary"
            loading={confirmLoading || confirming}
            disabled={confirmDisabled ?? internalConfirmDisabled}
            onClick={() => void handleConfirm()}
          >
            {confirmLabel}
          </Button>
        </Space>
      }
    >
      <div className={bodyClassName}>
        {notice}
        <Space>
          <StoreScopePickerSelect
            disabled={busy}
            onChange={(scopes) => {
              setAppliedStoreScopes(scopes);
              void loadPage(1, { storeScopes: scopes }).catch(() => undefined);
            }}
          />
          <Select
            className={optionAlignDropdownClassName}
            disabled={busy}
            value={searchType}
            style={{ width: 140 }}
            onChange={(next: SearchType) => {
              setSearchType(next);
              void loadPage(1, { type: next }).catch(() => undefined);
            }}
            options={[...searchTypeOptions]}
          />
          <Input
            disabled={busy}
            value={keyword}
            allowClear
            onChange={(event) => setKeyword(event.target.value)}
            onPressEnter={applySearch}
            placeholder={t('productKnowledge_scope_editor_search_placeholder')}
          />
          <Button type="primary" disabled={busy} icon={<SearchOutlined />} onClick={applySearch}>
            {t('productKnowledge_common_query')}
          </Button>
        </Space>
        {pageError ? (
          <Alert
            type="error"
            message={t('productKnowledge_scope_editor_search_failed')}
            action={
              <Button onClick={() => void loadPage(pageNo).catch(() => undefined)}>
                {t('productKnowledge_common_retry')}
              </Button>
            }
          />
        ) : null}
        <div className={listClassName}>
          <div className="document-scope-selected-row">
            <Typography.Text>
              {t('productKnowledge_mapping_selected_counts', {
                spu: selectedSpuCount,
                sku: selectedSkuCount,
              })}
            </Typography.Text>
            {headerExtra}
            <Button
              type="text"
              disabled={busy || confirming || !selected.size}
              onClick={() => setSelected(new Set())}
            >
              {t('productKnowledge_mapping_clear')}
            </Button>
          </div>
          <div className="document-scope-toolbar">
            <Checkbox
              checked={pageSelected}
              disabled={busy || confirming || !visibleProducts.length}
              onChange={() => (pageSelected ? clearPage() : selectPage())}
            >
              {t('productKnowledge_scope_editor_select_all_current')}
            </Checkbox>
            <div className="document-scope-toolbar-right">
              <span className="document-scope-only-selected">
                {t('productKnowledge_scope_editor_selected_only')}
                <Switch
                  checked={onlySelected}
                  disabled={busy || confirming}
                  aria-label={t('productKnowledge_scope_editor_selected_only')}
                  onChange={(checked) => setOnlySelected(checked)}
                />
              </span>
              <Button
                size="small"
                disabled={busy || confirming}
                onClick={() => setBulkVisible(true)}
              >
                {t('productKnowledge_scope_editor_bulk_select_short')}
              </Button>
              <Button
                size="small"
                disabled={busy || confirming || !items.length}
                onClick={toggleAllExpanded}
              >
                {t(
                  allExpanded
                    ? 'productKnowledge_scope_editor_collapse_sku'
                    : 'productKnowledge_scope_editor_expand_sku',
                )}
              </Button>
            </div>
          </div>
          <div className="document-scope-scroll">
            {!visibleProducts.length ? (
              <div className="document-scope-empty">
                <Typography.Text type="secondary">
                  {t('productKnowledge_scope_editor_no_data')}
                </Typography.Text>
              </div>
            ) : (
              <div className="document-scope-products">
                {visibleProducts.map((product) => (
                  <ProductSkuCard
                    key={productSelectionKey(product)}
                    product={product}
                    disabled={cardsDisabled}
                    selected={selected}
                    showSkus={keyMode === 'sku'}
                    expanded={expandedSpus.has(productSelectionKey(product))}
                    onToggleExpanded={(expandedProduct) =>
                      setExpandedSpus((current) => {
                        const key = productSelectionKey(expandedProduct);
                        const next = new Set(current);
                        if (next.has(key)) next.delete(key);
                        else next.add(key);
                        return next;
                      })
                    }
                    onToggleSku={toggle}
                    onToggleSpu={toggleSpu}
                  />
                ))}
              </div>
            )}
          </div>
          <ScopePagination
            pageNo={pageNo}
            pageSize={pageSize}
            total={total}
            hasNext={hasNext}
            disabled={busy || confirming}
            onPageChange={(page) => {
              setPageNo(page);
              void loadPage(page).catch(() => undefined);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              void loadPage(1, { size }).catch(() => undefined);
            }}
          />
        </div>
      </div>
      <JustRightHeightModal
        centered
        visible={bulkVisible}
        title={t('productKnowledge_scope_editor_bulk_select_title')}
        width={480}
        destroyOnClose
        onCancel={() => setBulkVisible(false)}
        onOk={() => void submitBulk()}
        okText={
          bulkMatching
            ? t('productKnowledge_scope_editor_searching')
            : t('productKnowledge_scope_editor_confirm_select')
        }
        okButtonProps={{ loading: bulkMatching }}
        cancelText={t('productKnowledge_common_cancel')}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>
              {t('productKnowledge_scope_editor_search_type_label')}
            </Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={bulkMatchType}
              onChange={(next: SearchType) => setBulkMatchType(next)}
              // 对齐 perseus：批量匹配刻意不含 TITLE（按名称批量匹配语义不明）
              options={searchTypeOptions
                .filter((option) => option.value !== 'TITLE')
                .map((option) => ({ label: option.label, value: option.value }))}
            />
          </div>
          <div>
            <Typography.Text strong>
              {t('productKnowledge_scope_editor_keyword_label')}
            </Typography.Text>
            <Input.TextArea
              style={{ marginTop: 4 }}
              rows={5}
              value={bulkKeywords}
              placeholder={t('productKnowledge_scope_editor_bulk_sku_placeholder')}
              onChange={(event) => setBulkKeywords(event.target.value)}
            />
          </div>
        </Space>
      </JustRightHeightModal>
    </JustRightHeightModal>
  );
}
